import { useState, useMemo } from 'react';
import { Accordion, AccordionSummary, AccordionDetails } from '@interstate/components/AccordionGroup';
import { Typography } from '@interstate/components/Typography';
import { useCustomerCardContext } from '../../CustomerFormContext';
import CurrentAddress from './CurrentAddress';
import PreviousAddress from './PreviousAddress';
import CurrentEmployment from './CurrentEmployment';
import PreviousEmployment from './PreviousEmployment';
import AdditionalIncome from './AdditionalIncome';
import { hasHousingAndEmploymentData } from './housingAndEmployment.utils';
import { SectionGroup } from './housingAndEmployment.styles';
import { CUSTOMER_CARD_ANALYTICS } from '../../../analytics';

/**
 * Housing & Employment Information section orchestrator.
 *
 * Top-level container that:
 * - Manages accordion expansion state
 * - Composes all housing and employment sub-sections
 * - Determines initial expansion based on whether data exists
 *
 * Structure:
 * - Current Address (housing status, mortgage/rent, duration)
 * - Previous Address (previous housing fields and duration)
 * - Current Employment (employment type, company, job title, duration)
 * - Previous Employment (collapsible with delete)
 * - Additional Income (collapsible with delete)
 *
 * All sub-sections read their own context data and manage their own state.
 * This orchestrator only manages the accordion expansion.
 */
export const HousingAndEmploymentInfo = () => {
    const { formValues, customerRole, onAnalyticsEvent } = useCustomerCardContext();

    // Initialize expansion based on whether any housing/employment data exists
    const hasData = useMemo(() => hasHousingAndEmploymentData(formValues), [formValues]);
    const [expanded, setExpanded] = useState(hasData);

    // Only the open counts as the tagged click (dc-sci-090) — collapsing stays silent.
    const handleToggle = () => {
        if (!expanded) {
            onAnalyticsEvent?.(CUSTOMER_CARD_ANALYTICS.CLICKED_HOUSING_EMPLOYMENT_ACCORDION, { customerRole });
        }
        setExpanded(!expanded);
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={handleToggle}
            data-testid="housing-employment-accordion"
            sx={{ border: 'none', marginBottom: '1rem', marginTop: '1.5rem' }}
        >
            <AccordionSummary chevronPosition="left">
                <Typography
                    variant="anchor-block-md"
                    color="base.color.blue.700"
                    data-testid="housing-employment-accordion-header"
                    sx={{ textWrap: 'auto' }}
                >
                    Housing & Employment Information
                </Typography>
            </AccordionSummary>

            <AccordionDetails data-testid="housing-employment-accordion-content">
                <SectionGroup>
                    <CurrentAddress />
                    <PreviousAddress />
                </SectionGroup>
                <SectionGroup>
                    <CurrentEmployment />
                    <PreviousEmployment />
                </SectionGroup>
                <SectionGroup>
                    <AdditionalIncome />
                </SectionGroup>
            </AccordionDetails>
        </Accordion>
    );
};

export default HousingAndEmploymentInfo;
